/**
 * SiteConfigPanel.js — Phase G
 * Tech Specialist UI to drive WordPress site identity, reading settings,
 * front page selection, and Customizer "Additional CSS" — all from OL.
 *
 * Backend: /api/wp-site-config/{org_id}   GET / PATCH
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import LiveSiteHeader from './LiveSiteHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIMEZONES = [
  'UTC', 'America/New_York', 'America/Chicago', 'America/Denver',
  'America/Los_Angeles', 'America/Phoenix', 'America/Anchorage',
  'Pacific/Honolulu', 'Europe/London', 'Europe/Paris',
];
const DATE_FORMATS = ['F j, Y', 'Y-m-d', 'm/d/Y', 'd/m/Y'];
const TIME_FORMATS = ['g:i a', 'H:i'];

export default function SiteConfigPanel({ orgId }) {
  const [cfg, setCfg] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [bridgeMissing, setBridgeMissing] = useState(false);
  const [wpDown, setWpDown] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true); setBridgeMissing(false); setWpDown(false); setErrMsg('');
    try {
      const { data } = await axios.get(`${API}/wp-site-config/${orgId}`, { withCredentials: true });
      setCfg(data); setDraft({});
    } catch (e) {
      const d = e.response?.data?.detail || '';
      if (e.response?.status === 503) setWpDown(true);
      else if (e.response?.status === 502 && /bridge_lacks/.test(d)) setBridgeMissing(true);
      else setErrMsg(d || e.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const value = (k) => draft[k] !== undefined ? draft[k] : (cfg?.[k] ?? '');
  const dirty = Object.keys(draft).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(
        `${API}/wp-site-config/${orgId}`,
        draft,
        { withCredentials: true },
      );
      setCfg(data); setDraft({}); setSavedAt(new Date());
    } catch (e) {
      setErrMsg(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  if (wpDown) return <BridgeBanner tone="red" title="WordPress site unreachable" body="The WP site is offline. Re-toggle the Live Link tunnel and click Retry." onRetry={load} />;
  if (bridgeMissing) return <BridgeBanner tone="amber" title="Bridge plugin needs SiteConfigController" body="The WP site is up but ol-bridge doesn't expose /site-config. Ship docs/wp-bridge-extensions/SITE_CONFIG.md and click Retry." onRetry={load} />;
  if (loading || !cfg) return <div className="p-6 text-sm text-gray-500">Loading…</div>;

  return (
    <div data-testid="site-config-panel" className="p-6 space-y-5">
      <LiveSiteHeader orgId={orgId} surfaceLabel="Site identity (title, tagline, logo, …)" />
      <div>
        <h2 className="text-lg font-semibold">WordPress Site Configuration</h2>
        <p className="text-sm text-gray-500">
          Edit the underlying WordPress site identity, reading settings,
          and Customizer &ldquo;Additional CSS&rdquo; — no wp-admin login required.
        </p>
      </div>

      <Group title="Site Identity">
        <Field2 label="Site title">
          <input data-testid="sc-site-title" value={value('site_title')} onChange={(e) => set('site_title', e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Tagline">
          <input data-testid="sc-tagline" value={value('tagline')} onChange={(e) => set('tagline', e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Admin email">
          <input data-testid="sc-admin-email" type="email" value={value('admin_email')} onChange={(e) => set('admin_email', e.target.value)} className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Site logo URL">
          <input data-testid="sc-logo" value={value('site_logo_url')} onChange={(e) => set('site_logo_url', e.target.value)} placeholder="Paste URL from Media Library" className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Site icon (favicon) URL">
          <input data-testid="sc-icon" value={value('site_icon_url')} onChange={(e) => set('site_icon_url', e.target.value)} placeholder="Paste URL from Media Library" className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
      </Group>

      <Group title="Time & Locale">
        <Field2 label="Timezone">
          <select data-testid="sc-timezone" value={value('timezone')} onChange={(e) => set('timezone', e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">(unchanged)</option>
            {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
          </select>
        </Field2>
        <Field2 label="Date format">
          <select data-testid="sc-date-format" value={value('date_format')} onChange={(e) => set('date_format', e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">(unchanged)</option>
            {DATE_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field2>
        <Field2 label="Time format">
          <select data-testid="sc-time-format" value={value('time_format')} onChange={(e) => set('time_format', e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">(unchanged)</option>
            {TIME_FORMATS.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </Field2>
        <Field2 label="Week starts on">
          <select data-testid="sc-start-of-week" value={value('start_of_week')} onChange={(e) => set('start_of_week', Number(e.target.value))} className="w-full border rounded px-3 py-2 text-sm">
            <option value={0}>Sunday</option>
            <option value={1}>Monday</option>
            <option value={6}>Saturday</option>
          </select>
        </Field2>
      </Group>

      <Group title="Reading">
        <Field2 label="Front page displays">
          <select data-testid="sc-show-on-front" value={value('show_on_front')} onChange={(e) => set('show_on_front', e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">(unchanged)</option>
            <option value="latest_posts">Your latest posts</option>
            <option value="page">A static page</option>
          </select>
        </Field2>
        <Field2 label="Front page (WP page ID)">
          <input data-testid="sc-page-on-front" type="number" value={value('page_on_front')} onChange={(e) => set('page_on_front', Number(e.target.value))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Posts page (WP page ID)">
          <input data-testid="sc-page-for-posts" type="number" value={value('page_for_posts')} onChange={(e) => set('page_for_posts', Number(e.target.value))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Posts per page">
          <input data-testid="sc-posts-per-page" type="number" value={value('posts_per_page')} onChange={(e) => set('posts_per_page', Number(e.target.value))} className="w-full border rounded px-3 py-2 text-sm" />
        </Field2>
        <Field2 label="Default comment status">
          <select data-testid="sc-comment-status" value={value('default_comment_status')} onChange={(e) => set('default_comment_status', e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">(unchanged)</option>
            <option value="open">open</option>
            <option value="closed">closed</option>
          </select>
        </Field2>
      </Group>

      <Group title="Customizer">
        <div className="col-span-2">
          <label className="block text-sm font-medium mb-1">Additional CSS</label>
          <textarea data-testid="sc-additional-css" value={value('additional_css')} onChange={(e) => set('additional_css', e.target.value)} rows={10} className="w-full border rounded px-3 py-2 text-sm font-mono" />
          <p className="text-xs text-gray-500 mt-1">Saved to the WordPress Customizer (same field as Appearance → Customize → Additional CSS).</p>
        </div>
      </Group>

      {errMsg && <div className="p-3 bg-red-50 text-red-800 text-xs rounded">{errMsg}</div>}

      <div className="flex items-center justify-between pt-2 border-t">
        <span className="text-xs text-gray-500">
          {dirty ? `${Object.keys(draft).length} unsaved change(s)` : 'No unsaved changes'}
          {savedAt && !dirty && ` · Saved at ${savedAt.toLocaleTimeString()}`}
        </span>
        <div className="flex gap-2">
          <button data-testid="sc-revert" onClick={() => setDraft({})} disabled={!dirty} className="px-3 py-2 border rounded text-sm disabled:opacity-50">Revert</button>
          <button data-testid="sc-save" onClick={save} disabled={!dirty || saving} className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">{saving ? 'Saving…' : 'Save & Publish to WP'}</button>
        </div>
      </div>
    </div>
  );
}

function Group({ title, children }) {
  return (
    <div className="border rounded p-4">
      <h3 className="text-sm font-medium mb-3 text-gray-700">{title}</h3>
      <div className="grid grid-cols-2 gap-4">{children}</div>
    </div>
  );
}

function Field2({ label, children }) {
  return (
    <div>
      <label className="block text-xs text-gray-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function BridgeBanner({ tone, title, body, onRetry }) {
  const cls = tone === 'red'
    ? 'bg-red-50 border-red-200 text-red-900'
    : 'bg-amber-50 border-amber-200 text-amber-900';
  const btn = tone === 'red' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700';
  return (
    <div className="p-6 text-sm">
      <div className={`p-4 rounded border ${cls}`}>
        <strong>{title}</strong>
        <p className="mt-1">{body}</p>
        <button data-testid="site-config-retry" onClick={onRetry} className={`mt-3 px-3 py-1 text-white rounded text-xs ${btn}`}>Retry</button>
      </div>
    </div>
  );
}
